import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Row, Col, Form, Image, Spinner, Button } from 'react-bootstrap';
import LoginImage from '../../assets/images/LoginImage.png';
import Logo from '../../assets/images/textLogo.svg';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { displayErrorToast } from '../../utils/ToastUtils';
import { APP_ENVIRONMENT_DEVELOPMENT } from '../../constants/app';
import config from '../../config';
import { useAppDispatch } from '../../store/store';
import { IncreaseResetStoreCounter } from '../../store/slice/common.slice';
import { SetUserDetails } from '../../store/slice/user.slice';
import { refreshToken } from '../../utils/auth.utils';
import { useLazyGetUserDetailsQuery } from '../../store/api/user.api';
import { IGetUserDetailsResponse } from '../../interfaces/user.interface';
import { SetOrganizationDetails } from '../../store/slice/organization.slice';
import { setUserAndAuthInitialState } from '../../utils/state.utils';

interface IProps {
    setCurrentEmail: Dispatch<SetStateAction<string>>;
}

const Login = ({ setCurrentEmail }: IProps) => {
    const navigate = useNavigate();
    const [getUserDetails] = useLazyGetUserDetailsQuery();
    const dispatch = useAppDispatch();
    const [username, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const togglePasswordVisibility = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        setPasswordVisible(!passwordVisible);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const isDevEnv = config.env === APP_ENVIRONMENT_DEVELOPMENT;
        if (!username) {
            displayErrorToast('Username is required.');
        }
        if (!password) {
            displayErrorToast('Password is required.');
            return;
        }

        if (isDevEnv && username.split('@').pop() !== 'pixii.ai') {
            displayErrorToast('Not Allowed');
            return;
        }

        setLoading(true);
        try {
            await Auth.signIn(username, password).then((data) => {
                dispatch(IncreaseResetStoreCounter());
            });
            await refreshToken(dispatch);
            const response = await getUserDetails();

            if (response.error && 'status' in response.error && response.error.status === 401) {
                await Auth.signOut();
                setUserAndAuthInitialState(dispatch);
                displayErrorToast('User Not verified');
                setLoading(false);
                navigate('/login', { replace: true });
            } else {
                const userData: IGetUserDetailsResponse = response.data;
                dispatch(
                    SetUserDetails({
                        id: userData.id,
                        name: userData.name,
                        email: userData.email,
                        stage: userData.stage,
                        currentlyWorkingBrandId: userData.currentlyWorkingBrandId,
                        apiStatus: userData.apiStatus,
                    }),
                );
                dispatch(
                    SetOrganizationDetails({
                        id: userData.organization.id,
                        name: userData.organization.name,
                        email: userData.organization.email,
                        subscriptionStatus: userData.organization.subscriptionStatus,
                    }),
                );
                navigate('/');
                setLoading(false);
            }
        } catch (e: any) {
            dispatch(IncreaseResetStoreCounter());
            if (e.name === 'UserNotConfirmedException') {
                setCurrentEmail(username);
                navigate('/verification');
            } else {
                displayErrorToast(e.message);
            }
            setLoading(false);
        }
    };

    return (
        <Row className="auth-page login d-flex align-items-center justify-content-center">
            <Col md={6} className="image-container">
                <div className="auth-page__image">
                    <Image src={LoginImage} />
                </div>
            </Col>
            <Col md={6} sm={12} className="form-container login-form">
                <div className="brand__logo">
                    <img src={Logo} alt="Logo" />
                </div>
                <Form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
                    <Form.Group controlId="loginEmail" className="form-alt">
                        <i className="pxi-email lg auth-icon"></i>
                        <Form.Label className="h3 fw-semibold text-primary">Email</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder=" Enter Your Email"
                            className="p-3"
                            onChange={(event) => setUserName(event?.target.value)}
                            autoFocus
                        />
                    </Form.Group>

                    <Form.Group controlId="loginPassword" className="form-alt">
                        <Form.Label className="h3 fw-semibold text-primary">Password</Form.Label>
                        <i className="ph ph-lock lg auth-icon"></i>
                        <Form.Control
                            type={passwordVisible ? 'text' : 'password'}
                            placeholder="Enter your Password"
                            className="input-field"
                            onChange={(event) => setPassword(event?.target.value)}
                        />
                        <Button
                            variant="white"
                            className="iconbtn form-btn"
                            onClick={togglePasswordVisibility}
                        >
                            {passwordVisible ? (
                                <i className="pxi-eye-close lg"></i>
                            ) : (
                                <i className="pxi-eye lg"></i>
                            )}
                        </Button>
                    </Form.Group>

                    <Button
                        type="submit"
                        size="lg"
                        variant="primary"
                        className="w-100 p-3 mt-4 mb-2"
                        disabled={loading}
                    >
                        {loading ? (
                            <Spinner animation="border" className="custom-spinner" />
                        ) : (
                            'Sign In'
                        )}
                    </Button>
                    <div className="d-flex justify-content-between text-primary fw-semibold">
                        {/* <label></label> */}
                        <p className="text-black fw-normal">
                            Don’t have an account?{' '}
                            <span
                                onClick={() => navigate('/signup')}
                                className="text-primary h3 fw-semibold"
                                style={{ cursor: 'pointer' }}
                            >
                                {' '}
                                Register here
                            </span>{' '}
                        </p>
                        <a
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/forgot_password');
                            }}
                        >
                            Forgot Password?
                        </a>
                    </div>
                </Form>
            </Col>
        </Row>
    );
};

export default Login;
